import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery } from '../../gql/hooks';
import {
  CLIENT_QUOTA_REPORT_QUERY,
  type ClientQuotaReportQueryResult,
  type ClientQuotaReportQueryVariables,
} from '../../gql/operations';
import { InlineBanner } from '../shared/InlineBanner';

// Dashboard alert for clients over their monthly quota (2026-08-09). Warn-mode quotas never
// block traffic — this banner IS the enforcement surface for them, so it sits at the top of
// the usage dashboard where an operator will actually see it. Renders nothing while loading,
// on error, or when every client is under budget (the quiet path costs one query per visit).

/** The current month as "YYYY-MM" (UTC — matches the backend's month bucketing). */
function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

export function ClientQuotaAlertBanner() {
  const { data } = useQuery<ClientQuotaReportQueryResult, ClientQuotaReportQueryVariables>(
    CLIENT_QUOTA_REPORT_QUERY,
    { month: currentMonth() },
  );

  const overCap = (data?.clientQuotaReport ?? []).filter(
    (row) => row.overTokenCap || row.overRequestCap,
  );

  if (overCap.length === 0) return null;

  return (
    <InlineBanner
      tone="warning"
      role="status"
      testId="client-quota-alert"
      icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
    >
      <strong>{overCap.length}</strong>{' '}
      {overCap.length === 1 ? 'client is' : 'clients are'} over their monthly quota:{' '}
      {overCap
        .map((row) => `${row.clientName} (${row.overTokenCap ? 'tokens' : 'requests'})`)
        .join(', ')}
      {' — '}
      <Link to="/reports/quotas" className="font-semibold underline underline-offset-2">
        view the monthly report
      </Link>
      .
    </InlineBanner>
  );
}
